/**
 * Super Admin–only assessment editor. Handles both create (id="new")
 * and edit (id=<uuid>) modes from the same sub-view per the spec
 * (Tickets 2 + 3 of the Assessments epic). Structure mirrors
 * AgentProfileView: outer <div className="space-y-6">, back link,
 * page header, then stacked section cards. NOT an inline panel and
 * NOT a modal.
 *
 * Edit-mode specifics:
 *  - Loads via getAssessment(id) and renders status pill + v{version}.
 *  - Editable Assigned teams section card below Measures (Ticket 7):
 *    searchable checkbox list, supadmin-only, disabled while Draft.
 *  - PATCH with If-Match header for optimistic locking.
 *  - When dropping a Saved assessment's measures to zero, an inline
 *    accent-coloured callout (per 0-design-appendix.md) names the
 *    teams that will be unassigned and gates the save behind a
 *    confirm step. No overlay modal.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  api,
  type AssessmentChangeLogEntry,
  type AssessmentChangeLogPage,
  type AssessmentDetail,
  type AssessmentInput,
  type AssessmentTeamRef,
  type MeasureInput,
  type TeamOut,
} from "../lib/api";
import { createAssessmentWithTeams } from "../lib/createAssessmentWithTeams";
import { editAssessmentWithTeams } from "../lib/editAssessmentWithTeams";
import { previewMeasuresImpactAndSave } from "../lib/previewMeasuresImpactAndSave";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { useConfirmDialog } from "../components/ConfirmDialog.helpers";

type MainSectionKey =
  | "details"
  | "ai"
  | "measures"
  | "teams"
  | "history"
  | "danger";

const ALL_SECTION_KEYS: MainSectionKey[] = [
  "details",
  "ai",
  "measures",
  "teams",
  "history",
  "danger",
];

type MultiLabelLabelDraft = {
  name: string;
  // The UI carries the phrase list as a single comma-separated string
  // and only splits / trims on submit (spec: "split/trim on submit,
  // join with ', ' on display"). Keeps editing snappy and avoids
  // disappearing-on-comma surprises.
  phrasesText: string;
};

type MultiLabelMeasureDraft = {
  uid: string; // local-only React key
  kind: "multi_label";
  label: string;
  labels: MultiLabelLabelDraft[];
};

let _uidCounter = 0;
const nextUid = () => `m${++_uidCounter}`;

function makeEmptyLabel(): MultiLabelLabelDraft {
  return { name: "", phrasesText: "" };
}

function makeEmptyMeasure(): MultiLabelMeasureDraft {
  return {
    uid: nextUid(),
    kind: "multi_label",
    label: "",
    labels: [makeEmptyLabel()],
  };
}

// Convert a server measure into the local draft shape. Only the
// multi_label kind is editable in this epic; other kinds (when they
// land) will need their own draft builders.
function measureToDraft(
  m: AssessmentDetail["measures"][number],
): MultiLabelMeasureDraft {
  const cfg = (m.config || {}) as {
    label?: string;
    labels?: { name?: string; detection_phrases?: string[] }[];
  };
  return {
    uid: nextUid(),
    kind: "multi_label",
    label: cfg.label ?? "",
    labels: (cfg.labels ?? []).map((l) => ({
      name: l.name ?? "",
      phrasesText: (l.detection_phrases ?? []).join(", "),
    })),
  };
}

type MeasureError = {
  label?: string;
  labels?: { name?: string; phrases?: string }[];
};

type FormErrors = {
  name?: string;
  ai_instructions?: string;
  measures?: MeasureError[];
};

const AI_INSTRUCTIONS_MAX = 10000;

function validate(
  name: string,
  aiInstructions: string,
  measures: MultiLabelMeasureDraft[],
): { errors: FormErrors; payload: AssessmentInput | null } {
  const errors: FormErrors = {};
  const trimmedName = name.trim();
  if (!trimmedName) errors.name = "Name is required";
  const trimmedAi = aiInstructions.trim();
  if (trimmedAi.length > AI_INSTRUCTIONS_MAX) {
    errors.ai_instructions = `AI Instructions must be ${AI_INSTRUCTIONS_MAX.toLocaleString()} characters or fewer (currently ${trimmedAi.length.toLocaleString()}).`;
  }

  const measurePayloads: MeasureInput[] = [];
  const measureErrors: MeasureError[] = measures.map(() => ({}));
  measures.forEach((m, mi) => {
    const merr: MeasureError = { labels: m.labels.map(() => ({})) };
    const trimmedLabel = m.label.trim();
    if (!trimmedLabel) merr.label = "Label is required";
    const labelPayloads: { name: string; detection_phrases: string[] }[] = [];
    m.labels.forEach((l, li) => {
      const lerr: { name?: string; phrases?: string } = {};
      const lname = l.name.trim();
      const phrases = l.phrasesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (!lname) lerr.name = "Label name is required";
      if (phrases.length === 0)
        lerr.phrases = "At least one detection phrase is required";
      merr.labels![li] = lerr;
      if (lname && phrases.length > 0) {
        labelPayloads.push({ name: lname, detection_phrases: phrases });
      }
    });
    if (m.labels.length === 0) {
      merr.label = merr.label ?? "At least one label is required";
    }
    measureErrors[mi] = merr;
    if (
      trimmedLabel &&
      labelPayloads.length === m.labels.length &&
      labelPayloads.length > 0
    ) {
      measurePayloads.push({
        kind: "multi_label",
        config: { label: trimmedLabel, labels: labelPayloads },
      });
    }
  });
  const anyMeasureError = measureErrors.some(
    (m) =>
      m.label ||
      (m.labels ?? []).some((l) => l.name || l.phrases),
  );
  if (anyMeasureError) errors.measures = measureErrors;

  const valid = !errors.name && !errors.ai_instructions && !errors.measures;
  if (!valid) return { errors, payload: null };
  const payload: AssessmentInput = {
    name: trimmedName,
    measures: measurePayloads,
  };
  if (trimmedAi) payload.ai_instructions = trimmedAi;
  return { errors, payload };
}

const INPUT_CLASS = "w-full border border-input rounded px-2 py-1.5 text-sm";
const INPUT_INVALID_CLASS =
  "w-full border border-destructive rounded px-2 py-1.5 text-sm";
const PRIMARY_BTN =
  "rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]";

export function AssessmentEditView({
  id,
  onBack,
  onCreated,
}: {
  /** "new" for create flow; an assessment id for edit. */
  id: "new" | string;
  onBack: () => void;
  onCreated: (assessment: AssessmentDetail) => void;
}) {
  const qc = useQueryClient();
  const dialog = useConfirmDialog();
  const isCreate = id === "new";

  const loadedQ = useQuery<AssessmentDetail>({
    queryKey: ["assessment", id],
    queryFn: () => api.getAssessment(id),
    enabled: !isCreate,
    refetchInterval: false,
  });
  const loaded = isCreate ? null : loadedQ.data ?? null;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [measures, setMeasures] = useState<MultiLabelMeasureDraft[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [unassignedNotice, setUnassignedNotice] = useState<
    AssessmentTeamRef[] | null
  >(null);
  const [pendingRevert, setPendingRevert] = useState<{
    teams: AssessmentTeamRef[];
  } | null>(null);
  // Task #55: preview-impact failures live in their own state so they
  // surface as an inline error in the Measures section (with a Retry
  // action) rather than as the generic submit-error banner. That makes
  // the recovery path obvious — the admin knows the assessment wasn't
  // saved and can retry the preview without reloading or adding a
  // measure back.
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Create-mode warning: admin picked teams while there are no
  // measures. The server creates the assessment as Draft and silently
  // ignores team assignments in that case, so we gate the save behind
  // an inline accent callout (matching the pendingRevert pattern) and
  // let them proceed or cancel.
  const [pendingNoMeasureTeams, setPendingNoMeasureTeams] = useState<
    TeamOut[] | null
  >(null);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  // Ticket 7: editable team assignments live alongside the rest of
  // the form state. ``initialTeamIds`` holds the server snapshot at
  // hydration time so we can diff on save and skip the PUT when the
  // set is unchanged.
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [initialTeamIds, setInitialTeamIds] = useState<string[]>([]);
  const [teamQuery, setTeamQuery] = useState("");
  // Tracks the post-create team-assignment PUT in the create flow.
  // The shared `saving` flag below includes this so the Save button
  // stays disabled across the whole create+assign sequence, preventing
  // a second click from firing a duplicate create while the team PUT
  // is in flight.
  const [isAssigningTeamsOnCreate, setIsAssigningTeamsOnCreate] =
    useState(false);

  // Collapsible state for the top-level sections. All start collapsed
  // so the page is easier to scan on first load; users expand only the
  // sections they want to edit. Sections containing a validation error
  // after a failed save attempt are force-opened via ``setOpenSections``
  // in ``onSave``.
  const [openSections, setOpenSections] = useState<Record<MainSectionKey, boolean>>({
    details: false,
    ai: false,
    measures: false,
    teams: false,
    history: false,
    danger: false,
  });
  const setSectionOpen = (key: MainSectionKey) => (next: boolean) =>
    setOpenSections((prev) => ({ ...prev, [key]: next }));
  const allExpanded = ALL_SECTION_KEYS.every((k) => openSections[k]);
  const toggleAllSections = () => {
    const next = !allExpanded;
    setOpenSections({
      details: next,
      ai: next,
      measures: next,
      teams: next,
      history: next,
      danger: next,
    });
  };

  const teamsQ = useQuery<TeamOut[]>({
    queryKey: ["teams"],
    queryFn: () => api.listTeams(),
    refetchInterval: false,
  });

  // Hydrate local form state once per loaded assessment id. Without
  // the guard, re-renders triggered by the user typing into an input
  // would clobber edits back to the server snapshot.
  useEffect(() => {
    if (!loaded) return;
    if (hydratedFor === loaded.id) return;
    setName(loaded.name);
    setDescription(loaded.description ?? "");
    setAiInstructions(loaded.ai_instructions ?? "");
    setMeasures(loaded.measures.map(measureToDraft));
    const ids = loaded.teams.map((t) => t.id);
    setSelectedTeamIds(ids);
    setInitialTeamIds(ids);
    setTeamQuery("");
    setErrors({});
    setSubmitError(null);
    setUnassignedNotice(null);
    setPendingRevert(null);
    setPendingNoMeasureTeams(null);
    setPreviewError(null);
    setHydratedFor(loaded.id);
  }, [loaded, hydratedFor]);

  // Auto-dismiss the no-measure-teams warning if the form no longer
  // matches the warning conditions (e.g. the admin added a measure or
  // deselected every team while the callout was visible). Avoids a
  // stale callout sitting under a now-valid form.
  useEffect(() => {
    if (!pendingNoMeasureTeams) return;
    if (measures.length > 0 || selectedTeamIds.length === 0) {
      setPendingNoMeasureTeams(null);
    }
  }, [pendingNoMeasureTeams, measures.length, selectedTeamIds.length]);

  const createM = useMutation({
    mutationFn: (body: AssessmentInput) =>
      api.createAssessment(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessments"] });
    },
    onError: (e) => {
      setSubmitError(
        (e as { message?: string })?.message || "Failed to save assessment",
      );
    },
  });

  const updateM = useMutation({
    mutationFn: (body: AssessmentInput) => {
      if (!loaded) throw new Error("assessment not loaded");
      return api.updateAssessment(
        loaded.id,
        {
          name: body.name,
          description: body.description ?? null,
          ai_instructions: body.ai_instructions ?? null,
          measures: body.measures,
        },
        loaded.version,
      );
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["assessments"] });
      qc.invalidateQueries({ queryKey: ["assessment", data.id] });
      // Re-hydrate the editable fields directly from the mutation's
      // fresh server snapshot (which may have normalized values, e.g.
      // trimmed whitespace). We deliberately do NOT clear
      // ``hydratedFor`` here: doing so would cause the hydration
      // effect to fire synchronously against the still-stale cached
      // ``loaded`` value (the invalidated query hasn't refetched yet),
      // clobbering the just-saved edits back to their pre-save values
      // — notably the AI Instructions textarea. Team state is left
      // alone; the subsequent team PUT (and its ``persistedTeamIds``)
      // is the source of truth for ``initialTeamIds``.
      setName(data.name);
      setDescription(data.description ?? "");
      setAiInstructions(data.ai_instructions ?? "");
      setMeasures(data.measures.map(measureToDraft));
      if (data.unassigned_teams && data.unassigned_teams.length > 0) {
        setUnassignedNotice(data.unassigned_teams);
      } else {
        setUnassignedNotice(null);
      }
      setPendingRevert(null);
    },
    onError: (e) => {
      setSubmitError(
        (e as { message?: string })?.message || "Failed to save assessment",
      );
    },
  });

  const teamsM = useMutation({
    mutationFn: (ids: string[]) => {
      if (!loaded) throw new Error("assessment not loaded");
      return api.setAssessmentTeams(loaded.id, ids);
    },
    onError: (e) => {
      setSubmitError(
        (e as { message?: string })?.message || "Failed to update teams",
      );
    },
  });

  const previewM = useMutation({
    mutationFn: (draftMeasures: MeasureInput[]) => {
      if (!loaded) throw new Error("assessment not loaded");
      return api.previewAssessmentImpact(
        loaded.id,
        draftMeasures,
      );
    },
    // Preview failures are surfaced via the inline ``previewError``
    // callout in the Measures section (Task #55), not the generic
    // submit-error banner. The helper's "preview_error" result carries
    // the message and ``onSave`` writes it into ``previewError``.
  });

  const updateMeasure = (uid: string, patch: Partial<MultiLabelMeasureDraft>) =>
    setMeasures((prev) =>
      prev.map((m) => (m.uid === uid ? { ...m, ...patch } : m)),
    );
  const updateLabel = (
    measureUid: string,
    labelIdx: number,
    patch: Partial<MultiLabelLabelDraft>,
  ) =>
    setMeasures((prev) =>
      prev.map((m) => {
        if (m.uid !== measureUid) return m;
        return {
          ...m,
          labels: m.labels.map((l, i) => (i === labelIdx ? { ...l, ...patch } : l)),
        };
      }),
    );
  const removeMeasure = (uid: string) =>
    setMeasures((prev) => prev.filter((m) => m.uid !== uid));
  const addMeasure = () =>
    setMeasures((prev) => [...prev, makeEmptyMeasure()]);
  const addLabel = (measureUid: string) =>
    setMeasures((prev) =>
      prev.map((m) =>
        m.uid === measureUid ? { ...m, labels: [...m.labels, makeEmptyLabel()] } : m,
      ),
    );
  const removeLabel = (measureUid: string, labelIdx: number) =>
    setMeasures((prev) =>
      prev.map((m) =>
        m.uid === measureUid
          ? { ...m, labels: m.labels.filter((_, i) => i !== labelIdx) }
          : m,
      ),
    );

  const teamsChanged = useMemo(() => {
    if (selectedTeamIds.length !== initialTeamIds.length) return true;
    const a = new Set(selectedTeamIds);
    return initialTeamIds.some((id) => !a.has(id));
  }, [selectedTeamIds, initialTeamIds]);

  const performSave = async (payload: AssessmentInput) => {
    const finalBody: AssessmentInput = {
      ...payload,
      ...(description.trim() ? { description: description.trim() } : {}),
    };
    if (isCreate) {
      let created: AssessmentDetail;
      try {
        created = await createM.mutateAsync(finalBody);
      } catch {
        return; // createM.onError already surfaced the message.
      }
      // The new assessment exists on the server now. If the user picked
      // teams and the assessment was created as Saved (i.e. it has at
      // least one measure), persist the team assignments. We can't
      // assign teams to a Draft assessment — the server would 422 — so
      // skip silently in that case (the picker is also disabled in the
      // UI when measures.length === 0, mirroring edit-mode behaviour).
      //
      // The team PUT is tracked via ``isAssigningTeamsOnCreate`` so
      // the shared ``saving`` flag stays true across the whole
      // create+assign sequence — otherwise a second Save click during
      // the PUT latency window would issue a duplicate create.
      //
      // The decision rules and partial-failure handling live in the
      // ``createAssessmentWithTeams`` helper so they can be exercised
      // by unit tests independently of React.
      const willAssignTeams =
        selectedTeamIds.length > 0 && created.status === "saved";
      if (willAssignTeams) setIsAssigningTeamsOnCreate(true);
      let teamAssignmentError: string | null = null;
      try {
        const result = await createAssessmentWithTeams(
          finalBody,
          selectedTeamIds,
          {
            // The POST already happened above via createM, so short-
            // circuit by returning the already-created assessment.
            // This keeps the integration-helper testable in isolation
            // (it can call createAssessment) while preserving the
            // existing mutation-driven query invalidation on the
            // create.
            createAssessment: async () => created,
            setAssessmentTeams: (id, ids) =>
              api.setAssessmentTeams(id, ids),
          },
        );
        teamAssignmentError = result.teamAssignmentError;
        if (result.teamPutCalled && !teamAssignmentError) {
          qc.invalidateQueries({ queryKey: ["teams"] });
          qc.invalidateQueries({ queryKey: ["assessment", created.id] });
          qc.invalidateQueries({ queryKey: ["assessments"] });
        }
      } finally {
        if (willAssignTeams) setIsAssigningTeamsOnCreate(false);
      }
      if (teamAssignmentError) {
        // The assessment was created but the team assignment failed.
        // Surface the error in the form's standard ``submitError``
        // banner *and* an in-app notice (since we're about to navigate
        // to edit mode, where the inline submitError state would be
        // re-mounted away). The admin can retry team assignment
        // from the editor.
        const fullMsg = `Assessment "${created.name}" was created, but team assignment failed: ${teamAssignmentError}. You can retry assigning teams from the assessment editor.`;
        setSubmitError(fullMsg);
        await dialog.notice({
          title: "Team assignment failed",
          message: fullMsg,
        });
      }
      onCreated(created);
      return;
    }
    // Edit: PATCH first (existing behaviour) and only PUT teams if
    // (a) the user changed the team set and (b) the assessment is
    // still Saved after the PATCH. The decision rules live in the
    // ``editAssessmentWithTeams`` helper so they can be exercised by
    // unit tests independently of React (Task #53). The mutations
    // are still used for their query-invalidation / loading-state
    // side-effects — the helper just orchestrates the call order.
    try {
      const result = await editAssessmentWithTeams(
        finalBody,
        selectedTeamIds,
        teamsChanged,
        {
          updateAssessment: (body) => updateM.mutateAsync(body),
          setAssessmentTeams: (_id, ids) => teamsM.mutateAsync(ids),
        },
      );
      if (result.teamPutCalled && result.persistedTeamIds) {
        // Keep the local "initial" set in sync so a second Save
        // without further edits is correctly a no-op.
        setInitialTeamIds(result.persistedTeamIds);
        qc.invalidateQueries({ queryKey: ["teams"] });
        qc.invalidateQueries({ queryKey: ["assessment", result.updated.id] });
      }
      // The PUT branch routes its error through teamsM.onError
      // (which sets submitError), matching the previous behaviour.
    } catch {
      return; // updateM.onError already surfaced the PATCH message.
    }
  };

  const toggleTeam = (teamId: string, checked: boolean) => {
    setSelectedTeamIds((prev) => {
      if (checked) return prev.includes(teamId) ? prev : [...prev, teamId];
      return prev.filter((id) => id !== teamId);
    });
  };

  const allTeams = useMemo(() => teamsQ.data ?? [], [teamsQ.data]);
  const filteredTeams = useMemo(() => {
    const q = teamQuery.trim().toLowerCase();
    if (!q) return allTeams;
    return allTeams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.community_name.toLowerCase().includes(q),
    );
  }, [allTeams, teamQuery]);

  const onSave = async () => {
    setSubmitError(null);
    setUnassignedNotice(null);
    setPreviewError(null);
    const { errors: errs, payload } = validate(name, aiInstructions, measures);
    setErrors(errs);
    if (!payload) {
      // Force-open any section that now has a validation error so the
      // admin can see what needs fixing, without collapsing sections
      // they've expanded themselves.
      setOpenSections((prev) => ({
        ...prev,
        details: prev.details || !!errs.name,
        ai: prev.ai || !!errs.ai_instructions,
        measures: prev.measures || !!errs.measures,
      }));
      return;
    }
    // Create mode: if the admin picked teams but there are no
    // measures, the server will create the assessment as Draft and
    // silently ignore the team assignments. Gate the save behind an
    // inline accent callout naming the affected teams so they can
    // either proceed (and assign teams later) or cancel and add
    // measures first.
    if (isCreate && payload.measures.length === 0 && selectedTeamIds.length > 0) {
      const selectedSet = new Set(selectedTeamIds);
      const teamsForWarning = (teamsQ.data ?? []).filter((t) =>
        selectedSet.has(t.id),
      );
      setPendingNoMeasureTeams(teamsForWarning);
      return;
    }
    // Edit mode: if measures dropped to zero on a Saved assessment,
    // call preview-impact first and gate the save behind the inline
    // confirm callout. The server is still authoritative — this is a
    // UX warning only. The decision rules live in the
    // ``previewMeasuresImpactAndSave`` helper so they can be
    // exercised by unit tests independently of React (Task #54).
    if (!isCreate && loaded) {
      const result = await previewMeasuresImpactAndSave(
        payload,
        loaded.status,
        {
          previewImpact: (measures) => previewM.mutateAsync(measures),
          performSave,
        },
      );
      if (result.kind === "needs_confirm") {
        setPendingRevert({ teams: result.teams });
      } else if (result.kind === "preview_error") {
        // Surface the failure inline in the Measures section with a
        // Retry action (Task #55). The Save button stays enabled so
        // the admin can simply hit Save again, or click Retry on the
        // callout to re-run just the preview.
        setPreviewError(result.error);
      }
      // "saved" needs no further action — the helper called
      // performSave for us.
      return;
    }
    void performSave(payload);
  };

  const onConfirmRevert = () => {
    const { payload } = validate(name, aiInstructions, measures);
    if (!payload) return;
    setPendingRevert(null);
    void performSave(payload);
  };

  const onConfirmNoMeasureTeams = () => {
    const { payload } = validate(name, aiInstructions, measures);
    if (!payload) return;
    setPendingNoMeasureTeams(null);
    void performSave(payload);
  };

  const titleText = useMemo(() => {
    if (isCreate) return "New assessment";
    if (loaded) return `Edit assessment: ${loaded.name}`;
    return "Edit assessment";
  }, [isCreate, loaded]);
  const helperText =
    "Measures are the structured signals this assessment extracts from each call — together they make up what the assessment reports on. Add one or more measures to define what to capture. With zero measures, the assessment is saved as Draft.";

  const saving =
    createM.isPending
    || updateM.isPending
    || previewM.isPending
    || teamsM.isPending
    || isAssigningTeamsOnCreate;

  if (!isCreate && loadedQ.isLoading && !loaded) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to assessments
        </button>
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!isCreate && loadedQ.error) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to assessments
        </button>
        <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {(loadedQ.error as { message?: string })?.message
            || "Failed to load assessment"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to assessments
      </button>

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-foreground">{titleText}</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleAllSections}
            className="text-sm text-primary hover:underline"
            aria-expanded={allExpanded}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
          {!isCreate && loaded && (
            <div className="flex items-center gap-2">
              <AssessmentStatusPill status={loaded.status} />
              <span className="text-xs text-muted-foreground">
                v{loaded.version}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Details */}
      <CollapsibleSection
        title="Details"
        open={openSections.details}
        onOpenChange={setSectionOpen("details")}
      >
        <label className="block text-xs text-muted-foreground">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`mt-1 ${errors.name ? INPUT_INVALID_CLASS : INPUT_CLASS}`}
          />
          {errors.name && (
            <span className="block mt-1 text-xs text-destructive">
              {errors.name}
            </span>
          )}
        </label>
        <label className="block text-xs text-muted-foreground">
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`mt-1 ${INPUT_CLASS}`}
          />
        </label>
      </CollapsibleSection>

      {/* AI Instructions */}
      <CollapsibleSection
        title={
          <div>
            <h3 className="font-semibold text-foreground">AI Instructions</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Describe, in plain language, how the AI should evaluate each call
              for this assessment — the logic or rubric it should follow. This
              is where you teach the assessment what to look for.
            </p>
          </div>
        }
        open={openSections.ai}
        onOpenChange={setSectionOpen("ai")}
      >
        <textarea
          value={aiInstructions}
          onChange={(e) => setAiInstructions(e.target.value)}
          rows={8}
          placeholder="e.g. For each call, identify whether the rep clearly stated pricing, handled objections, and confirmed next steps. Flag any compliance-sensitive language..."
          className={errors.ai_instructions ? INPUT_INVALID_CLASS : INPUT_CLASS}
        />
        {errors.ai_instructions && (
          <span className="block text-xs text-destructive">
            {errors.ai_instructions}
          </span>
        )}
      </CollapsibleSection>

      {/* Measures */}
      <CollapsibleSection
        title={
          <div>
            <h3 className="font-semibold text-foreground">Measures</h3>
            <p className="text-xs text-muted-foreground mt-1">{helperText}</p>
          </div>
        }
        open={openSections.measures}
        onOpenChange={setSectionOpen("measures")}
      >
        {measures.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            No measures yet — the assessment will be saved as Draft.
          </div>
        ) : (
          <div className="space-y-3">
            {measures.map((m, mi) => {
              const merr = errors.measures?.[mi];
              return (
                <div
                  key={m.uid}
                  className="rounded border border-input p-3 bg-muted/30 space-y-2"
                >
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => removeMeasure(m.uid)}
                      className="text-destructive hover:underline text-xs"
                    >
                      Remove
                    </button>
                  </div>
                  <label className="block text-xs text-muted-foreground">
                    Label
                    <input
                      type="text"
                      value={m.label}
                      onChange={(e) =>
                        updateMeasure(m.uid, { label: e.target.value })
                      }
                      placeholder="e.g. Objection types"
                      className={`mt-1 ${merr?.label ? INPUT_INVALID_CLASS : INPUT_CLASS}`}
                    />
                    {merr?.label && (
                      <span className="block mt-1 text-xs text-destructive">
                        {merr.label}
                      </span>
                    )}
                  </label>
                  <div className="space-y-2">
                    {m.labels.map((l, li) => {
                      const lerr = merr?.labels?.[li];
                      return (
                        <div
                          key={li}
                          className="flex items-start gap-2"
                        >
                          <div className="flex-1">
                            <input
                              type="text"
                              value={l.name}
                              onChange={(e) =>
                                updateLabel(m.uid, li, { name: e.target.value })
                              }
                              placeholder="Label name"
                              className={lerr?.name ? INPUT_INVALID_CLASS : INPUT_CLASS}
                            />
                            {lerr?.name && (
                              <span className="block mt-1 text-xs text-destructive">
                                {lerr.name}
                              </span>
                            )}
                          </div>
                          <div className="flex-[2]">
                            <input
                              type="text"
                              value={l.phrasesText}
                              onChange={(e) =>
                                updateLabel(m.uid, li, {
                                  phrasesText: e.target.value,
                                })
                              }
                              placeholder="Detection phrases, comma-separated"
                              className={lerr?.phrases ? INPUT_INVALID_CLASS : INPUT_CLASS}
                            />
                            {lerr?.phrases && (
                              <span className="block mt-1 text-xs text-destructive">
                                {lerr.phrases}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeLabel(m.uid, li)}
                            className="text-destructive hover:underline text-xs pt-1.5"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => addLabel(m.uid)}
                      className="text-sm text-primary hover:underline"
                    >
                      + Add label
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {previewError && (
          <div
            role="alert"
            className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <div>
              Couldn't check which teams would be unassigned — please retry.
            </div>
            <div className="mt-1 text-xs opacity-80">{previewError}</div>
            <div className="mt-2">
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="text-sm font-medium text-destructive hover:underline disabled:opacity-60"
              >
                {previewM.isPending ? "Retrying…" : "Retry"}
              </button>
            </div>
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={addMeasure}
            className={PRIMARY_BTN}
          >
            + Add measure
          </button>
        </div>
      </CollapsibleSection>

      {/* Assigned teams (Ticket 7) — supadmin-editable picker. Goes
          after Measures and before the action bar / Danger Zone. In
          create mode the picker is shown before save too, but disabled
          while there are no measures (since the assessment would be
          created as Draft and the team-assignment endpoint rejects
          Draft assessments). */}
      {(isCreate || loaded) && (() => {
        const effectiveStatus: "draft" | "saved" = isCreate
          ? (measures.length === 0 ? "draft" : "saved")
          : (loaded?.status ?? "draft");
        const pickerDisabled = effectiveStatus === "draft";
        return (
        <CollapsibleSection
          title="Assigned teams"
          rightAdornment={
            pickerDisabled ? (
              <span className="text-xs text-muted-foreground">
                {isCreate
                  ? "Add at least one measure to assign teams"
                  : "Save with at least one measure first"}
              </span>
            ) : undefined
          }
          open={openSections.teams}
          onOpenChange={setSectionOpen("teams")}
        >
          <input
            type="search"
            placeholder="Search teams"
            value={teamQuery}
            onChange={(e) => setTeamQuery(e.target.value)}
            className="w-full border border-input rounded px-2 py-1.5 text-sm"
          />

          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto rounded border border-input p-2 bg-muted/30">
            {teamsQ.isLoading ? (
              <span className="px-2 py-1 text-sm text-muted-foreground">
                Loading teams…
              </span>
            ) : filteredTeams.length === 0 ? (
              <span className="px-2 py-1 text-sm text-muted-foreground">
                No teams to show.
              </span>
            ) : (
              filteredTeams.map((t) => {
                const checked = selectedTeamIds.includes(t.id);
                const disabled = pickerDisabled;
                return (
                  <label
                    key={t.id}
                    className={`w-full flex items-center gap-2 px-2 py-1 text-sm hover:bg-muted/40 ${
                      checked ? "bg-muted/40" : ""
                    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleTeam(t.id, e.target.checked)}
                      disabled={disabled}
                    />
                    <span className="font-medium">{t.name}</span>
                    <span className="text-xs text-muted-foreground">
                      — {t.community_name}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </CollapsibleSection>
        );
      })()}

      {submitError && (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>
      )}

      {unassignedNotice && unassignedNotice.length > 0 && (
        <div className="rounded border border-accent/40 bg-accent/10 p-3 text-sm text-accent-foreground">
          This assessment was reverted to Draft and unassigned from:{" "}
          <strong>{unassignedNotice.map((t) => t.name).join(", ")}</strong>.
        </div>
      )}

      {pendingNoMeasureTeams && (
        <div className="rounded border border-accent/40 bg-accent/10 p-3 text-sm text-accent-foreground">
          You've selected{" "}
          <strong>
            {pendingNoMeasureTeams.length === 0
              ? "teams"
              : pendingNoMeasureTeams.map((t) => t.name).join(", ")}
          </strong>
          , but this assessment has no measures yet. It will be saved as{" "}
          <strong>Draft</strong> and{" "}
          <strong>no teams will be assigned</strong> until you add at least one
          measure. Continue?
          <div className="mt-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setPendingNoMeasureTeams(null)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Keep editing
            </button>
            <button
              type="button"
              onClick={onConfirmNoMeasureTeams}
              className={PRIMARY_BTN}
            >
              Save as Draft
            </button>
          </div>
        </div>
      )}

      {pendingRevert && (
        <div className="rounded border border-accent/40 bg-accent/10 p-3 text-sm text-accent-foreground">
          Removing all measures will revert this assessment to{" "}
          <strong>Draft</strong> and unassign it from:{" "}
          <strong>
            {pendingRevert.teams.length === 0
              ? "no teams"
              : pendingRevert.teams.map((t) => t.name).join(", ")}
          </strong>
          . Existing call results are preserved. Continue?
          <div className="mt-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setPendingRevert(null)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Keep editing
            </button>
            <button
              type="button"
              onClick={onConfirmRevert}
              className={PRIMARY_BTN}
            >
              Yes, save
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className={`${PRIMARY_BTN} disabled:opacity-60`}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {!isCreate && loaded && (
        <AssessmentHistorySection
          assessmentId={loaded.id}
          open={openSections.history}
          onOpenChange={setSectionOpen("history")}
        />
      )}

      {!isCreate && loaded && (
        <DangerZone
          assessmentId={loaded.id}
          assessmentName={loaded.name}
          open={openSections.danger}
          onOpenChange={setSectionOpen("danger")}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ["assessments"] });
            onBack();
          }}
        />
      )}

      {dialog.element}
    </div>
  );
}

const PAGE_SIZE = 20;

function AssessmentHistorySection({
  assessmentId,
  open,
  onOpenChange,
}: {
  assessmentId: string;
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}) {
  const [pages, setPages] = useState<AssessmentChangeLogPage[]>([]);
  const [offset, setOffset] = useState(0);

  // Reset when navigating between assessments.
  useEffect(() => {
    setPages([]);
    setOffset(0);
  }, [assessmentId]);

  const pageQ = useQuery<AssessmentChangeLogPage>({
    queryKey: ["assessment-changes", assessmentId, offset],
    queryFn: () =>
      api.assessmentChanges(assessmentId, PAGE_SIZE, offset),
    refetchInterval: false,
  });

  useEffect(() => {
    if (!pageQ.data) return;
    setPages((prev) => {
      // Replace if we already loaded the same offset (e.g. on refetch).
      const filtered = prev.filter(
        (p) => (p.next_offset ?? p.total) - p.entries.length !== offset,
      );
      return [...filtered, pageQ.data!];
    });
  }, [pageQ.data, offset]);

  const entries: AssessmentChangeLogEntry[] = pages.flatMap((p) => p.entries);
  const latest = pages[pages.length - 1];
  const total = latest?.total ?? 0;
  const hasMore = latest?.next_offset != null;

  return (
    <CollapsibleSection
      title="History"
      open={open}
      onOpenChange={onOpenChange}
      defaultOpen={false}
      rightAdornment={
        total > 0 ? (
          <span className="text-xs text-muted-foreground">
            {entries.length} of {total}
          </span>
        ) : undefined
      }
    >
      {pageQ.isLoading && entries.length === 0 && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {pageQ.error && entries.length === 0 && (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          {(pageQ.error as { message?: string })?.message
            || "Failed to load history"}
        </div>
      )}

      {!pageQ.isLoading && entries.length === 0 && !pageQ.error && (
        <div className="text-xs text-muted-foreground italic">
          No changes recorded yet.
        </div>
      )}

      {entries.length > 0 && (
        <ol className="space-y-2">
          {entries.map((e) => (
            <li
              key={e.id}
              className="rounded border border-input bg-muted/30 p-3 text-sm"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium text-foreground">
                  {changeKindLabel(e.change_kind)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatTimestamp(e.changed_at)}
                </span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                by {actorLabel(e)}
              </div>
              <div className="mt-2 text-xs text-foreground">
                {renderDiff(e)}
              </div>
            </li>
          ))}
        </ol>
      )}

      {hasMore && (
        <div>
          <button
            type="button"
            onClick={() => setOffset(latest!.next_offset!)}
            disabled={pageQ.isFetching}
            className="text-sm text-primary hover:underline disabled:opacity-60"
          >
            {pageQ.isFetching ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </CollapsibleSection>
  );
}

/**
 * Super Admin–only soft-delete affordance shown at the bottom of the
 * edit view (Ticket 4). Per spec this is a destructive *text link* in
 * its own section card — never a red button. Confirmation uses
 * in-app confirmation dialog matching the TeamsView delete pattern.
 */
function DangerZone({
  assessmentId,
  assessmentName,
  onDeleted,
  open,
  onOpenChange,
}: {
  assessmentId: string;
  assessmentName: string;
  onDeleted: () => void;
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}) {
  const dialog = useConfirmDialog();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const preview = await api.previewDeleteAssessment(assessmentId);
      const teamNames = preview.teams_to_unassign.map((t) => t.name);
      const message =
        teamNames.length === 0
          ? `Delete assessment "${assessmentName}"? This cannot be undone.`
          : `Delete assessment "${assessmentName}"? This will unassign it from: ${teamNames.join(", ")}. Existing call results are preserved. This cannot be undone.`;
      const ok = await dialog.confirm({
        title: "Delete assessment?",
        message,
        confirmLabel: "Delete",
        destructive: true,
      });
      if (!ok) return;
      await api.deleteAssessment(assessmentId);
      onDeleted();
    } catch (e) {
      setError(
        (e as { message?: string })?.message || "Failed to delete assessment",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <CollapsibleSection
      title="Danger zone"
      open={open}
      onOpenChange={onOpenChange}
      defaultOpen={false}
    >
      <p className="text-xs text-muted-foreground">
        Deleting an assessment removes it from any assigned teams. Existing
        call results are preserved. This cannot be undone.
      </p>
      <button
        type="button"
        onClick={handleDelete}
        disabled={busy}
        className="text-destructive hover:underline disabled:opacity-60"
      >
        {busy ? "Deleting…" : "Delete assessment"}
      </button>
      {error && (
        <div className="text-xs text-destructive">{error}</div>
      )}
      {dialog.element}
    </CollapsibleSection>
  );
}

function changeKindLabel(kind: string): string {
  switch (kind) {
    case "created":
      return "Created";
    case "name":
      return "Name changed";
    case "description":
      return "Description changed";
    case "ai_instructions":
      return "AI Instructions changed";
    case "measures":
      return "Measures changed";
    case "status":
      return "Status changed";
    case "team_assignments":
      return "Team assignments changed";
    case "deleted":
      return "Deleted";
    default:
      return kind;
  }
}

function actorLabel(e: AssessmentChangeLogEntry): string {
  return e.changed_by_name || e.changed_by_email || "Unknown user";
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

type AnyRec = Record<string, unknown>;

function asRec(v: unknown): AnyRec | null {
  return v && typeof v === "object" ? (v as AnyRec) : null;
}

function renderDiff(e: AssessmentChangeLogEntry): ReactNode {
  const before = asRec(e.before);
  const after = asRec(e.after);
  switch (e.change_kind) {
    case "created":
      return (
        <span className="text-muted-foreground">
          Assessment created
          {after && typeof after.name === "string" ? ` as "${after.name}"` : ""}
          .
        </span>
      );
    case "name": {
      const oldName = String(before?.name ?? "");
      const newName = String(after?.name ?? "");
      return (
        <>
          <span className="text-muted-foreground">Name: </span>
          <span className="line-through text-muted-foreground">{oldName || "—"}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="text-foreground">{newName || "—"}</span>
        </>
      );
    }
    case "description": {
      const oldDesc =
        before && before.description != null ? String(before.description) : "";
      const newDesc =
        after && after.description != null ? String(after.description) : "";
      return (
        <>
          <span className="text-muted-foreground">Description: </span>
          <span className="line-through text-muted-foreground">
            {oldDesc || "—"}
          </span>
          <span className="text-muted-foreground"> → </span>
          <span className="text-foreground">{newDesc || "—"}</span>
        </>
      );
    }
    case "ai_instructions": {
      const oldAi =
        before && before.ai_instructions != null
          ? String(before.ai_instructions)
          : "";
      const newAi =
        after && after.ai_instructions != null
          ? String(after.ai_instructions)
          : "";
      if (!oldAi && newAi) {
        return (
          <span className="text-muted-foreground">AI Instructions set.</span>
        );
      }
      if (oldAi && !newAi) {
        return (
          <span className="text-muted-foreground">AI Instructions cleared.</span>
        );
      }
      return (
        <span className="text-muted-foreground">AI Instructions updated.</span>
      );
    }
    case "status": {
      const oldStatus = String(before?.status ?? "");
      const newStatus = String(after?.status ?? "");
      if (oldStatus === "saved" && newStatus === "draft") {
        return <span>Reverted to Draft</span>;
      }
      if (oldStatus === "draft" && newStatus === "saved") {
        return <span>Promoted to Saved</span>;
      }
      return (
        <span>
          Status: {oldStatus || "—"} → {newStatus || "—"}
        </span>
      );
    }
    case "measures":
      return <MeasuresDiff before={before?.measures} after={after?.measures} />;
    case "team_assignments": {
      const oldTeams = teamNames(before?.teams);
      const newTeams = teamNames(after?.teams);
      const removed = oldTeams.filter((t) => !newTeams.includes(t));
      const added = newTeams.filter((t) => !oldTeams.includes(t));
      const parts: string[] = [];
      if (removed.length > 0) parts.push(`Unassigned from ${removed.join(", ")}`);
      if (added.length > 0) parts.push(`Assigned to ${added.join(", ")}`);
      if (parts.length === 0) return <span>Team assignments updated</span>;
      return <span>{parts.join("; ")}</span>;
    }
    case "deleted":
      return <span className="text-muted-foreground">Assessment deleted</span>;
    default:
      return <span className="text-muted-foreground">Updated</span>;
  }
}

type MeasureShape = { label: string; labels: string[] };

function extractMeasures(value: unknown): MeasureShape[] {
  if (!Array.isArray(value)) return [];
  return value.map((m) => {
    const rec = asRec(m);
    const cfg = asRec(rec?.config) ?? {};
    const rawLabels = Array.isArray(cfg.labels) ? (cfg.labels as unknown[]) : [];
    const labels = rawLabels.map((l) => {
      const lr = asRec(l);
      return lr && typeof lr.name === "string" ? lr.name : "";
    });
    return {
      label: typeof cfg.label === "string" ? cfg.label : "",
      labels,
    };
  });
}

function diffNameList(
  before: string[],
  after: string[],
  itemKind: string,
): string[] {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const removed = before.filter((n) => !afterSet.has(n));
  const added = after.filter((n) => !beforeSet.has(n));
  if (removed.length === 1 && added.length === 1) {
    return [
      `Renamed ${itemKind}: ${removed[0] || "—"} → ${added[0] || "—"}`,
    ];
  }
  const lines: string[] = [];
  for (const a of added) lines.push(`Added ${itemKind}: ${a || "—"}`);
  for (const r of removed) lines.push(`Removed ${itemKind}: ${r || "—"}`);
  return lines;
}

function diffMeasures(before: MeasureShape[], after: MeasureShape[]): string[] {
  const lines: string[] = [];
  const beforeByName = new Map(before.map((m) => [m.label, m]));
  const afterByName = new Map(after.map((m) => [m.label, m]));
  const matched: { b: MeasureShape; a: MeasureShape; displayName: string }[] = [];
  const unmatchedBefore: MeasureShape[] = [];
  const unmatchedAfter: MeasureShape[] = [];
  for (const bm of before) {
    const am = afterByName.get(bm.label);
    if (am) matched.push({ b: bm, a: am, displayName: am.label });
    else unmatchedBefore.push(bm);
  }
  for (const am of after) {
    if (!beforeByName.has(am.label)) unmatchedAfter.push(am);
  }
  const soleBefore = unmatchedBefore[0];
  const soleAfter = unmatchedAfter[0];
  if (
    unmatchedBefore.length === 1 &&
    unmatchedAfter.length === 1 &&
    soleBefore !== undefined &&
    soleAfter !== undefined
  ) {
    const bm = soleBefore;
    const am = soleAfter;
    lines.push(
      `Renamed measure: ${bm.label || "—"} → ${am.label || "—"}`,
    );
    matched.push({ b: bm, a: am, displayName: am.label || bm.label });
  } else {
    for (const am of unmatchedAfter) {
      lines.push(`Added measure: ${am.label || "—"}`);
    }
    for (const bm of unmatchedBefore) {
      lines.push(`Removed measure: ${bm.label || "—"}`);
    }
  }
  for (const { b, a, displayName } of matched) {
    const labelLines = diffNameList(b.labels, a.labels, "label");
    const prefix = displayName || "—";
    for (const l of labelLines) {
      lines.push(`${prefix}: ${l.charAt(0).toLowerCase()}${l.slice(1)}`);
    }
  }
  return lines;
}

function MeasuresDiff({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}) {
  const [expanded, setExpanded] = useState(false);
  const beforeMs = extractMeasures(before);
  const afterMs = extractMeasures(after);
  const lines = diffMeasures(beforeMs, afterMs);
  if (lines.length === 0) {
    return (
      <span>
        Measures: {beforeMs.length} → {afterMs.length}
      </span>
    );
  }
  const COLLAPSED = 3;
  const showAll = expanded || lines.length <= COLLAPSED;
  const visible = showAll ? lines : lines.slice(0, COLLAPSED);
  return (
    <div className="space-y-1">
      <ul className="list-disc list-inside space-y-0.5">
        {visible.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
      {lines.length > COLLAPSED && (
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? "Show less"
            : `Show ${lines.length - COLLAPSED} more`}
        </button>
      )}
    </div>
  );
}

function teamNames(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    const rec = asRec(item);
    if (rec && typeof rec.name === "string") out.push(rec.name);
  }
  return out;
}

function AssessmentStatusPill({ status }: { status: "draft" | "saved" }) {
  const cls = status === "saved"
    ? "bg-primary/15 text-primary"
    : "bg-accent/20 text-accent-foreground";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}
